import { useState, useEffect, useMemo } from "react";
import { useActionSnippet } from "../../../messaging/useActionSnippet";
import { loadConditionLabels, loadSideLabels, conditionLabel, sideLabel } from "../../helpers";
import { evaluatePricing, applyPricingReview, money } from "../../models/price-drawer/quotePricingPolicy";
import { storedBasePrice } from "../../models/price-drawer/quoteEditingPolicy";
import { injectPartsTemplate, buildQuoteSharePayload } from "../../models/price-drawer/quoteShareModel";
import { useQuoteReviewCorrection } from "./useQuoteApprovalFlow";
import { useQuoteShareCommands } from "./useQuoteShareCommands";
import { useQuoteRevision } from '../../../payments-v2/viewmodels/useQuoteRevision';

export function useQuoteShare({ open, onClose, card, taskCustomer, taskCustomerType, parts, quotePricing, costPrices, pricingTimeline, onStalePricing, authState, userId, userMap, customerPhone, readOnly, preloadedPayload, onApprovalCreated, resubmissionReason, onSuggestUnavailableNotice, confirmUnavailable, notify }) {
  const revision = useQuoteRevision(card?.id, open && !readOnly);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState("b2c");
  const { snippet: actionSnippet, loading: qrLoading, error: bindingError } = useActionSnippet(open && !readOnly, `quote_${activeTab}`);
  const [includedPartIds, setIncludedPartIds] = useState(new Set());
  const [partSp, setPartSp] = useState({}); // { [partId]: number } — overrideable SP per part
  // One global VAT mode/% applied to every included part in this send —
  // no per-part VAT choice anymore (was `partVat`, a per-part map).
  const [vatMode, setVatMode] = useState("excl");
  const [vatPct, setVatPct] = useState(5);
  const [partOldExchange, setPartOldExchange] = useState({}); // { [partId]: boolean }
  const [deliveryAmount, setDeliveryAmount] = useState(0);
  // Floor for deliveryAmount — the amount can be raised but never dropped
  // below what it started at, since delivery is no longer optional.
  const [minDeliveryAmount, setMinDeliveryAmount] = useState(0);
  const [messageText, setMessageText] = useState("");
  const [textEditEnabled, setTextEditEnabled] = useState(false);
  const [textManuallyEdited, setTextManuallyEdited] = useState(false);
  // Bumped when the condition code→label map arrives so labels replace raw codes
  const [condVersion, setCondVersion] = useState(0);

  const { reviewCorrection, setReviewCorrection, pricingNotice, setPricingNotice, receiveReview } = useQuoteReviewCorrection({
    getPricingKey: () => pricingKey, setManualShare: value => setManualShare(value), onStalePricing, setTextManuallyEdited, notify,
  });

  // customer_type is numeric: 0 = B2B, 1 = B2C, null = unknown
  // taskCustomerType is extracted from the full task fetch and is the most reliable source
  const rawType = readOnly
    ? (preloadedPayload?.customer_type ?? null)
    : (taskCustomerType ?? taskCustomer?.type ?? taskCustomer?.customer_type ?? card?.customer_type ?? card?.customer?.type ?? card?.customer?.customer_type ?? null);
  const isB2B = rawType === 0 || rawType === "0";
  const isB2C = rawType === 1 || rawType === "1";
  const typeKnown = isB2B || isB2C;

  useEffect(() => {
    if (!open) return;
    loadConditionLabels().then(() => setCondVersion(v => v + 1));
    loadSideLabels().then(() => setCondVersion(v => v + 1));
    if (readOnly && preloadedPayload) {
      // Hydrate from the stored payload snapshot
      const sp = {};
      const oldExchange = {};
      const included = new Set();
      const firstPart = (preloadedPayload.parts ?? [])[0];
      // vat_mode is the current field; fall back to the old boolean for
      // payload snapshots saved before the Excl. VAT option existed. Every
      // part in one send shares the same mode/% going forward — the first
      // included part's values are a reasonable single representative for
      // older payloads that might still vary per part.
      setVatMode(firstPart ? (firstPart.vat_mode ?? (firstPart.vat_enabled ? "incl" : "none")) : "excl");
      setVatPct(firstPart?.vat_pct ?? 5);
      (preloadedPayload.parts ?? []).forEach(p => {
        sp[p.query_part_id] = p.sp;
        oldExchange[p.query_part_id] = !!p.old_exchange;
        if (p.included) included.add(p.query_part_id);
      });
      setPartSp(sp);
      setPartOldExchange(oldExchange);
      setIncludedPartIds(included);
      setDeliveryAmount(preloadedPayload.delivery_amount ?? 0);
      setDeliveryEnabled(!!preloadedPayload.delivery_enabled);
      setMinDeliveryAmount(preloadedPayload.delivery_amount ?? 0);
      setActiveTab(preloadedPayload.template_type ?? "b2c");
      setMessageText(preloadedPayload.message_text ?? "");
      setTextManuallyEdited(true); // suppress any re-injection
      setTextEditEnabled(false);
      setSending(false);
      return;
    }
    setReviewCorrection(null); setPricingNotice("");
    setActiveTab(isB2B ? "b2b" : "b2c");
    setIncludedPartIds(new Set(parts.filter(p => p.selected_quote_id).map(p => p.id)));
    const sp = {};
    const oldExchange = {};
    parts.forEach(p => {
      const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
      sp[p.id] = quotePricing[p.selected_quote_id]?.sp ?? storedBasePrice(p, quote, costPrices);
      // Forward-compatible: not persisted on query_parts yet (see
      // FUTURE_TASKS.md), so this is always false today, but reading it
      // means nothing else needs to change once the backend adds it.
      oldExchange[p.id] = !!p.old_exchange;
    });
    setPartSp(sp);
    setVatMode("excl");
    setVatPct(5);
    setPartOldExchange(oldExchange);
    setDeliveryAmount(0);
    setDeliveryEnabled(false);
    setMinDeliveryAmount(0);
    setTextEditEnabled(false);
    setTextManuallyEdited(false);
    setSending(false);
  }, [open]); // eslint-disable-line

  const pricingInputs = parts.filter(p => p.selected_quote_id).map(p => {
    const q = (p.supplier_quotes || []).find(row => row.id === p.selected_quote_id);
    return { query_part_id: p.id, part_name: p.part_name, quote_id: p.selected_quote_id, included: includedPartIds.has(p.id),
      cp: Number(q?.quoted_price || 0), base_sp: partSp[p.id] ?? quotePricing[p.selected_quote_id]?.sp ?? Number(q?.quoted_price || 0) };
  });
  const pricingKey = JSON.stringify(pricingInputs);
  const localReview = evaluatePricing(pricingInputs, costPrices, pricingTimeline);
  const pricingReview = reviewCorrection?.key === pricingKey ? { ...reviewCorrection.review, parts: applyPricingReview(localReview.parts, reviewCorrection.review) } : localReview;
  const pricedById = new Map(pricingReview.parts.map(p => [Number(p.query_part_id), p]));
  const pricingVersionKey = JSON.stringify(pricingReview.parts);
  const selectedPartsForInjection = useMemo(() => {
    if (readOnly && preloadedPayload) {
      return (preloadedPayload.parts ?? [])
        .filter(p => p.included)
        .map(p => ({
          part: { part_name: p.part_name, condition_preference: p.condition },
          quote: { warranty_days: p.warranty_days, quoted_price: p.cp },
          sp: p.sp,
          vatPct: p.vat_pct ?? 5,
          vatMode: p.vat_mode ?? (p.vat_enabled ? "incl" : "none"),
          oldExchange: partOldExchange[p.query_part_id] ?? !!p.old_exchange,
        }));
    }
    return parts
      .filter(p => includedPartIds.has(p.id) && p.selected_quote_id)
      .map(p => {
        const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        const sp = pricedById.get(Number(p.id))?.sp ?? partSp[p.id];
        return { part: p, quote, sp, vatPct, vatMode, oldExchange: !!partOldExchange[p.id] };
      })
      .filter(({ quote }) => !!quote);
  }, [readOnly, preloadedPayload, parts, includedPartIds, partSp, vatPct, vatMode, partOldExchange, pricingVersionKey]); // eslint-disable-line

  const deliveryPerPart = selectedPartsForInjection.length > 0
    ? money((deliveryEnabled ? deliveryAmount || 0 : 0) / selectedPartsForInjection.length)
    : 0;

  useEffect(() => {
    if (!open || readOnly || textManuallyEdited) return;
    const qr = actionSnippet;
    if (!qr) { setMessageText(""); return; }
    setMessageText(injectPartsTemplate(qr.body, card, selectedPartsForInjection, deliveryPerPart, { conditionLabel, sideLabel }, { customer: taskCustomer || card?.customer, authState, userMap, customerPhone }));
  }, [activeTab, selectedPartsForInjection, actionSnippet, open, readOnly, deliveryPerPart, condVersion, taskCustomer, card, authState, userMap, customerPhone]); // eslint-disable-line

  const togglePart = (partId) => {
    setIncludedPartIds(prev => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId); else next.add(partId);
      return next;
    });
  };

  const toggleOldExchange = (partId) =>
    setPartOldExchange(prev => ({ ...prev, [partId]: !prev[partId] }));


  const getPayload = () => revision.attach(buildQuoteSharePayload({ parts, includedPartIds, pricedById, vatPct, vatMode, deliveryPerPart, partOldExchange, conditionLabel, card, customerPhone, rawType, userId, authState, activeTab, messageText, textManuallyEdited, deliveryEnabled, deliveryAmount, resubmissionReason }));
  const { sending, setSending, manualShare, setManualShare, recordingManual, handleSend, handleManualShare } = useQuoteShareCommands({ actionSnippet, qrLoading, bindingError, userId, card, selectedPartsForInjection, messageText, getPayload, onApprovalCreated, parts, onSuggestUnavailableNotice, confirmUnavailable, onClose, receiveReview, notify });
  return { revision, deliveryEnabled, setDeliveryEnabled, activeTab, setActiveTab, actionSnippet, qrLoading, bindingError, includedPartIds, partSp, setPartSp, vatMode, setVatMode, vatPct, setVatPct, partOldExchange, deliveryAmount, setDeliveryAmount, minDeliveryAmount, messageText, setMessageText, textEditEnabled, setTextEditEnabled, textManuallyEdited, setTextManuallyEdited, isB2B, isB2C, typeKnown, pricingNotice, pricingReview, pricedById, selectedPartsForInjection, deliveryPerPart, togglePart, toggleOldExchange, sending, manualShare, setManualShare, recordingManual, handleSend, handleManualShare };
}
